// Recording-to-encounter identity rule. Encounter record ids are recycled after
// the backend hard-deletes trashed encounters, so a recording queued for a deleted
// encounter can resolve to a brand-new encounter for a different patient. Keep the
// first two rules identical to the backend's lib/recordingIdentityGuard.js (same
// reason strings): the server applies them again in `Transcript - Save`.
// Spec: docs/superpowers/specs/2026-10-12-recording-encounter-identity-guard-design.md

// Clock skew between the device and the server; identical to the backend (6 hours).
export const IDENTITY_GRACE_MS = 6 * 60 * 60 * 1000;
// Longest a recording may predate an encounter it claims to belong to by offline id.
export const IDENTITY_OFFLINE_CAP_MS = 7 * 24 * 60 * 60 * 1000;
export const IDENTITY_MAX_AGE_MS = 15 * 24 * 60 * 60 * 1000;
// A recording captured less than this long ago on this device skips the client fetch.
export const IDENTITY_FRESH_MS = 60 * 60 * 1000;
// Upper bound on the client's one identity getChat; a timeout is transient.
export const IDENTITY_FETCH_TIMEOUT_MS = 20 * 1000;
export const IDENTITY_ERROR_CODE = "recording-identity-mismatch";
export const IDENTITY_REFUSED = "identity";
export const IDENTITY_REASONS = Object.freeze({
  PREDATES_ENCOUNTER: "predates-encounter",
  PATIENT_MISMATCH: "patient-mismatch",
  TOO_OLD: "too-old",
  ENCOUNTER_MISMATCH: "encounter-mismatch",
});

const TEMP_PREFIX = "temp-";

const isBlank = (value) => {
  if (value == null) return true;
  const s = String(value).trim();
  return s === "" || s === "null" || s === "undefined";
};

export function identityTimeMs(value) {
  if (isBlank(value)) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value.getTime === "function") {
    const t = Number(value.getTime());
    return Number.isFinite(t) ? t : null;
  }
  const s = String(value).trim();
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  const parsed = Date.parse(s);
  return Number.isFinite(parsed) ? parsed : null;
}

const isTrue = (value) => value === true || String(value) === "true";

// The encounter's own UUID, minted by the backend when the encounter is created and
// never reused (record ids are). Stored as the context var `local:encounterUuid`; read
// tolerantly across the shapes a chat row can come back in, like offlineIdOf.
export const encounterUuidOf = (row) => {
  const cv = row?.contextVariables || {};
  const value = cv["local:encounterUuid"] || cv.encounterUuid || row?.encounterUuid;
  return isBlank(value) ? null : String(value);
};

// recording: { startedAt, patientUserId, encounterUuid, chatId (the id it was queued under), overrideIdentity }
// chat:      { id, userId, createdOn, offlineId, encounterUuid }
export function evaluateRecordingIdentity(
  recording,
  chat,
  { nowMs = Date.now(), graceMs = IDENTITY_GRACE_MS, maxAgeMs = IDENTITY_MAX_AGE_MS, autoDrain = false } = {}
) {
  const r = recording || {};
  const c = chat || {};
  if (isTrue(r.overrideIdentity)) return { ok: true };

  // A recording that knows its encounter's UUID is judged on that alone: the UUID is
  // never recycled, so a match is the same encounter (whatever its age, or a patient
  // re-assignment since) and anything else is not -- including an encounter that
  // carries no UUID at all, which cannot be the one the recording was made for. A
  // recording captured without a UUID (older build, offline-created encounter) falls
  // through to the time and patient rules below, exactly as before.
  if (!isBlank(r.encounterUuid)) {
    return String(r.encounterUuid) === encounterUuidOf(c)
      ? { ok: true }
      : { ok: false, reason: IDENTITY_REASONS.ENCOUNTER_MISMATCH };
  }

  const startedAt = identityTimeMs(r.startedAt);
  const createdOn = identityTimeMs(c.createdOn);
  // Offline exemption: the recording CLAIMS it was queued under a temp- id (the never-
  // recycled offline UUID of its encounter). The server stamps createdOn at sync time,
  // after the recording started, so the start-time rule cannot apply -- but only within
  // IDENTITY_OFFLINE_CAP_MS, and only when the chat's own offline id equals the claim
  // (the server demands an exact match; an unknown offline id parks, never exempts).
  const claimsOffline = typeof r.chatId === "string" && r.chatId.startsWith(TEMP_PREFIX);
  const chatOffline = isBlank(c.offlineId) ? null : String(c.offlineId);
  const offlineEncounter =
    claimsOffline &&
    startedAt != null &&
    createdOn != null &&
    createdOn - startedAt <= IDENTITY_OFFLINE_CAP_MS &&
    chatOffline === r.chatId;
  if (!offlineEncounter && startedAt != null && createdOn != null && startedAt < createdOn - graceMs) {
    return { ok: false, reason: IDENTITY_REASONS.PREDATES_ENCOUNTER };
  }
  if (!isBlank(r.patientUserId) && !isBlank(c.userId) && String(r.patientUserId) !== String(c.userId)) {
    return { ok: false, reason: IDENTITY_REASONS.PATIENT_MISMATCH };
  }
  if (autoDrain && startedAt != null && nowMs - startedAt > maxAgeMs) {
    return { ok: false, reason: IDENTITY_REASONS.TOO_OLD };
  }
  return { ok: true };
}

export function isIdentityRefusal(outcome) {
  return !!outcome && typeof outcome === "object" && outcome.refused === IDENTITY_REFUSED;
}
